/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutLoyaltyAdd, ICheckoutLoyaltyRewardPoint, ICheckoutLoyaltyShow,
    ICheckoutLoyaltyViewProps
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout-loyalty/./checkout-loyalty';
import MsDyn365 from "@msdyn365-commerce/core";
import { ICheckoutLoyaltyProps } from "../definition-extensions/checkout-loyalty.ext.props.autogenerated";


interface ICheckoutLoyaltyAddExtended extends ICheckoutLoyaltyAdd {
    notEnoughPointsErrorString: string;
    showNotEnoughPointsError: boolean;
}

const AddCheckoutLoyalty: React.FC<ICheckoutLoyaltyAddExtended> = ({
    heading,
    cardNumber,
    drawer,
    rewardPoints,
    loyaltyAmountContainer,
    appliedLoyaltyAmountContainer,
    loyaltyAmountLabel,
    loyaltyAmountInput,
    loyaltyAmountApplyButton,
    showAppliedAmount,
    loyaltyAmountAppliedText,
    loyaltyAmountAppliedPrice,
    loyaltyAmountRemoveButton,
  notEnoughPointsErrorString,
  showNotEnoughPointsError
}) => {
    return (
        <>
            {heading}
            {cardNumber}
            <Node {...drawer}>
                {rewardPoints.map((rewardPoint: ICheckoutLoyaltyRewardPoint) => {
                    return (
                        <Node {...rewardPoint.checkoutLoyaltyRewardPoint} key={rewardPoint.key}>
                            {rewardPoint.title}
                            {rewardPoint.availablePointsText}
                            {rewardPoint.activePoints}
                            {rewardPoint.expiringPointsText}
                            {rewardPoint.expiringPoints}
                        </Node>
                    );
                })}
            </Node>
            <Node {...loyaltyAmountContainer}>
                {loyaltyAmountLabel}
                {loyaltyAmountInput}
                {loyaltyAmountApplyButton}
                {
                    showAppliedAmount &&
                        <Node {...appliedLoyaltyAmountContainer}>
                            {loyaltyAmountAppliedText}
                            {loyaltyAmountAppliedPrice}
                            {loyaltyAmountRemoveButton}
                        </Node>
                }
            </Node>
            {showNotEnoughPointsError && <p>{notEnoughPointsErrorString}</p>}
        </>
    );
};

const ShowCheckoutLoyalty: React.FC<ICheckoutLoyaltyShow> = ({
    heading,
    coveredAmountText,
    amount
}) => {

    return (
        <>
            {heading}
            {coveredAmountText}
            {amount}
        </>
    );
};

const CheckoutLoyaltyView: React.FC<ICheckoutLoyaltyViewProps & ICheckoutLoyaltyProps<{}>> = props => {
    const [ initialized , setInitialized ] = React.useState<boolean>(false);
    const [ showMinValueError , setShowMinvalueError ] = React.useState<boolean>(false);
    const { checkoutLoyalty,
        viewState,
        showLoyalty,
        addLoyalty,
      resources: {
          notEnoughPointsErrorString,
          notEnoughPointsMessage
      },
      config: {
        minLoyaltyRedeemAmount
      },
      context: {
        cultureFormatter: {
            formatCurrency
        }
      }
    } = props;
    const maxDollars = getMaxDollars(props);
    const minDollars = minLoyaltyRedeemAmount ? (minLoyaltyRedeemAmount * .10) : 0;
    const minPoints  = minLoyaltyRedeemAmount || 0;
    if (MsDyn365.isBrowser && !initialized) {
        const input = document.querySelector<HTMLInputElement>('.ms-checkout-loyalty__amount-input');
        const btn = document.querySelector<HTMLButtonElement>('.ms-checkout-loyalty__amount-button');
        if (input && btn) {
            setInitialized(true);
            loyaltyGuard(minDollars, input, btn, setShowMinvalueError);
        }

    }

    const addLoyaltyExtended: ICheckoutLoyaltyAddExtended = {
        ...addLoyalty,
        notEnoughPointsErrorString: notEnoughPointsErrorString.replace('{0}', formatCurrency(minDollars)),
        showNotEnoughPointsError: showMinValueError
    }

    let cartDollars = props.data.checkout.result?.checkoutCart.cart.TotalAmount || 0;
    return (
        <Module {...checkoutLoyalty}>
            {minDollars > cartDollars ? (
               <>
               <p>{formatCartValueTooLowMessage(props.resources.cartValueTooLowMessage, minPoints.toString(), formatCurrency(minDollars), formatCurrency(cartDollars))}</p>
               </> 
            ) : 
            maxDollars >= minDollars || minDollars === 0 ? (
                <>
                  {viewState.isShowLoyalty && <ShowCheckoutLoyalty {...showLoyalty} />}
                  {viewState.isShowAddLoyalty && <AddCheckoutLoyalty {...addLoyaltyExtended} />}
                </>
              ) : (
                <>
                <p>{formatNotEnoughPointsMessage(notEnoughPointsMessage, minPoints.toString(), formatCurrency(minDollars), formatCurrency(maxDollars))}</p>
                </>)
            }

        </Module>
    );
};

const formatNotEnoughPointsMessage = (message: string, minPoints:string, minDollars: string, maxDollars: string) =>
  message.replace('{0}', minPoints).replace('{1}', minDollars).replace('{2}', maxDollars);

  const formatCartValueTooLowMessage = (message: string, minPoints: string, equivalentDollars:string, cartDollars: string) =>
  formatNotEnoughPointsMessage(message, minPoints, equivalentDollars, cartDollars);


const getMaxDollars = (props: ICheckoutLoyaltyViewProps): number => {
    return props.data.loyaltyTransactionEstimation.result &&
    props.data.loyaltyTransactionEstimation.result.MaxCurrencyValueOfLoyaltyPoints ? props.data.loyaltyTransactionEstimation.result.MaxCurrencyValueOfLoyaltyPoints : 0;
}

const loyaltyGuard = (minDollars: number, input: HTMLInputElement, button: HTMLButtonElement, setShowMinValueError:  React.Dispatch<React.SetStateAction<boolean>>) => {
    input.addEventListener('keyup', (e) => {
        // @ts-ignore - yes it is
        const value = e.target?.value;
        if (value) {
            const parsedValue = parseFloat(value);
            const isError = parsedValue < minDollars;
            button.disabled = isError;
            setShowMinValueError(isError);
        }
    })
}

export default CheckoutLoyaltyView;
